import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PremiumGate } from "@/components/PremiumGate";
import axios from "axios";
import {
  BarChart2, TrendingUp, CheckCircle, Users, Search, ChevronDown, ChevronUp, ArrowUpDown, ChevronLeft, AlertCircle,
} from "lucide-react";
import { format, subDays, parseISO } from "date-fns";
import { it } from "date-fns/locale";

const API = process.env.REACT_APP_BACKEND_URL;

const PERIODS = [
  { id: "1m", label: "1M" },
  { id: "3m", label: "3M" },
  { id: "6m", label: "6M" },
  { id: "1y", label: "1A" },
  { id: "all", label: "Tutto" },
];

const OUTCOME_META = {
  order:      { label: "Fatto Ordine",  color: "bg-emerald-500", light: "bg-emerald-50 text-emerald-700" },
  visit:      { label: "Fatto Visita",  color: "bg-blue-500",    light: "bg-blue-50 text-blue-700" },
  not_found:  { label: "Non Trovato",   color: "bg-amber-400",   light: "bg-amber-50 text-amber-700" },
  not_passed: { label: "Non Passato",   color: "bg-red-400",     light: "bg-red-50 text-red-700" },
};

// ── KPI Card ──────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, accent }) {
  return (
    <div className={`bg-white rounded-2xl border-2 ${accent} p-4 shadow-sm`}>
      <div className="text-xs text-slate-400 mb-1">{label}</div>
      <div className="text-2xl font-extrabold text-[#0F172A] leading-tight">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

// ── Monthly bar chart ─────────────────────────────────────────────────────────
function MonthlyChart({ data }) {
  if (!data || !data.length)
    return <p className="text-slate-400 text-sm text-center py-8">Nessun ordine nel periodo selezionato</p>;

  const maxAmount = Math.max(...data.map(d => d.amount), 1);
  const total = data.reduce((s, d) => s + d.amount, 0);
  const totalOrders = data.reduce((s, d) => s + d.count, 0);

  return (
    <div>
      <div className="flex items-end gap-2 h-36 mb-2">
        {data.map(d => {
          const label = format(parseISO(d.month + "-01"), "MMM yy", { locale: it });
          const h = Math.max((d.amount / maxAmount) * 85, d.count > 0 ? 8 : 0);
          return (
            <div key={d.month} className="flex-1 flex flex-col items-center gap-1 min-w-0">
              {d.amount > 0 && (
                <span className="text-[9px] text-slate-500 font-medium truncate w-full text-center">
                  {d.amount >= 1000 ? `${(d.amount / 1000).toFixed(1)}k` : d.amount.toFixed(0)}
                </span>
              )}
              <div
                title={`${label}: ${d.count} ordini · €${d.amount.toFixed(2)}`}
                className="w-full rounded-t bg-[#0F172A] hover:bg-slate-600 transition-colors cursor-pointer"
                style={{ height: `${h}%` }}
              />
              <span className="text-[9px] text-slate-400 truncate w-full text-center">{label}</span>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-xs text-slate-500 pt-2 border-t border-slate-100">
        <span>{totalOrders} ordini totali</span>
        <span className="font-bold text-[#0F172A]">€{total.toFixed(2)}</span>
      </div>
    </div>
  );
}

// ── Daily bar chart (ultimi 30gg) ─────────────────────────────────────────────
function DailyChart({ data }) {
  const days = Array.from({ length: 30 }, (_, i) => {
    const d = format(subDays(new Date(), 29 - i), "yyyy-MM-dd");
    const found = data.find(x => x.date === d);
    return { date: d, count: found?.count || 0, amount: found?.amount || 0 };
  });
  const maxAmount = Math.max(...days.map(d => d.amount), 1);
  const hasData = days.some(d => d.count > 0);

  if (!hasData)
    return <p className="text-slate-400 text-sm text-center py-8">Nessun ordine negli ultimi 30 giorni</p>;

  return (
    <div>
      <div className="flex items-end gap-0.5 h-24 mb-1">
        {days.map(d => (
          <div key={d.date} className="flex-1">
            <div
              title={`${d.date}: ${d.count} ordini · €${d.amount.toFixed(0)}`}
              className={`w-full rounded-t transition-colors ${d.count > 0 ? "bg-violet-500 hover:bg-violet-400" : "bg-transparent"}`}
              style={{ height: `${Math.max((d.amount / maxAmount) * 100, d.count > 0 ? 8 : 0)}%` }}
            />
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-slate-400">
        <span>{format(subDays(new Date(), 29), "d MMM", { locale: it })}</span>
        <span>Oggi</span>
      </div>
    </div>
  );
}

// ── Outcomes breakdown ────────────────────────────────────────────────────────
function OutcomesBreakdown({ data }) {
  const total = Object.values(data).reduce((s, v) => s + v, 0);
  if (!total)
    return <p className="text-slate-400 text-sm text-center py-8">Nessun esito nel periodo selezionato</p>;

  return (
    <div className="space-y-4">
      {/* Stacked progress bar */}
      <div className="flex h-6 rounded-xl overflow-hidden gap-0.5">
        {Object.entries(OUTCOME_META).map(([key, meta]) => {
          const pct = (data[key] / total) * 100;
          if (!pct) return null;
          return (
            <div key={key} className={`${meta.color} transition-all`} style={{ width: `${pct}%` }}
              title={`${meta.label}: ${data[key]}`} />
          );
        })}
      </div>

      {/* Legend rows */}
      <div className="space-y-2.5">
        {Object.entries(OUTCOME_META).map(([key, meta]) => {
          const n = data[key];
          const pct = total > 0 ? Math.round((n / total) * 100) : 0;
          return (
            <div key={key} className="flex items-center gap-3">
              <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${meta.color}`} />
              <span className="text-sm text-slate-700 flex-1">{meta.label}</span>
              {/* mini bar */}
              <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden hidden sm:block">
                <div className={`h-full rounded-full ${meta.color}`} style={{ width: `${pct}%` }} />
              </div>
              <span className="text-xs text-slate-400 w-8 text-right">{pct}%</span>
              <span className="font-bold text-[#0F172A] w-8 text-right">{n}</span>
            </div>
          );
        })}
      </div>

      <div className="pt-2 border-t border-slate-100 flex justify-between text-xs text-slate-400">
        <span>{total} visite totali</span>
        <span>{data.order} ordini · tasso {total > 0 ? Math.round(data.order / total * 100) : 0}%</span>
      </div>
    </div>
  );
}

// ── Client stat row ───────────────────────────────────────────────────────────
function ClientStatRow({ cs, expanded, onToggle }) {
  const targetPct = cs.target_annual && cs.year_order_amount
    ? Math.min(Math.round((cs.year_order_amount / cs.target_annual) * 100), 100)
    : null;
  const avgOrder = cs.total_orders > 0 ? (cs.total_order_amount / cs.total_orders) : 0;

  return (
    <div className="border-b border-slate-50 last:border-0">
      <button
        onClick={onToggle}
        data-testid={`stat-client-row-${cs.client_id}`}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm text-[#0F172A] truncate">{cs.client_name}</div>
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">{cs.total_visits} visite</span>
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-violet-50 text-violet-700">{cs.total_orders} ordini</span>
            {cs.conversion_rate > 0 && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">{cs.conversion_rate}%</span>
            )}
            {cs.total_order_amount > 0 && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">€{cs.total_order_amount.toFixed(0)}</span>
            )}
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0 ml-2" /> : <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0 ml-2" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 bg-slate-50 space-y-3">
          <div className="grid grid-cols-2 gap-2 pt-2">
            <div className="bg-white rounded-xl p-3 border border-slate-100">
              <div className="text-xs text-slate-400">Media per ordine</div>
              <div className="font-bold text-[#0F172A]">€{avgOrder.toFixed(2)}</div>
            </div>
            <div className="bg-white rounded-xl p-3 border border-slate-100">
              <div className="text-xs text-slate-400">1 ordine ogni</div>
              <div className="font-bold text-[#0F172A]">{cs.visits_per_order ?? "—"} visite</div>
            </div>
            <div className="bg-white rounded-xl p-3 border border-slate-100">
              <div className="text-xs text-slate-400">Totale ordini</div>
              <div className="font-bold text-[#0F172A]">€{cs.total_order_amount.toFixed(2)}</div>
            </div>
            <div className="bg-white rounded-xl p-3 border border-slate-100">
              <div className="text-xs text-slate-400">Ultima visita</div>
              <div className="font-bold text-[#0F172A] text-sm">
                {cs.last_visit ? new Date(cs.last_visit).toLocaleDateString("it-IT") : "—"}
              </div>
            </div>
          </div>

          {cs.target_annual && (
            <div className="bg-white rounded-xl p-3 border border-slate-100">
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-slate-500">Target {new Date().getFullYear()}</span>
                <span className="font-semibold text-[#0F172A]">€{cs.year_order_amount?.toFixed(0)} / €{cs.target_annual}</span>
              </div>
              <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${targetPct >= 100 ? "bg-emerald-500" : targetPct >= 60 ? "bg-amber-400" : "bg-red-400"}`}
                  style={{ width: `${targetPct || 0}%` }}
                />
              </div>
              <div className="text-right text-xs text-slate-400 mt-1">{targetPct ?? 0}%</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
function StatsContent() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState("6m");
  const [tab, setTab] = useState("summary");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("orders");
  const [expanded, setExpanded] = useState(null);

  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setStats(null);
    axios
      .get(`${API}/api/stats`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { period },
      })
      .then(r => setStats(r.data))
      .catch(() => setError("Impossibile caricare le statistiche. Controlla la connessione e riprova."))
      .finally(() => setLoading(false));
  }, [token, period, refresh]);

  const filteredClients = (stats?.client_stats || [])
    .filter(c => c.total_visits > 0)
    .filter(c => !search || c.client_name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === "revenue")    return b.total_order_amount - a.total_order_amount;
      if (sortBy === "conversion") return b.conversion_rate - a.conversion_rate;
      if (sortBy === "visits")     return b.total_visits - a.total_visits;
      return b.total_orders - a.total_orders;
    });

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-2xl mx-auto pb-10">

      {/* Header + back button + period selector */}
      <div className="space-y-3">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#0F172A] transition-colors -ml-1"
          data-testid="stats-back-btn"
        >
          <ChevronLeft className="w-4 h-4" />
          Indietro
        </button>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-extrabold text-[#0F172A]">Statistiche</h1>
            <p className="text-slate-400 text-sm">Report vendite e performance</p>
          </div>
          <div className="flex gap-1 bg-slate-100 rounded-xl p-1 flex-shrink-0">
            {PERIODS.map(p => (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                data-testid={`period-${p.id}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  period === p.id ? "bg-white text-[#0F172A] shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-6 h-6 border-2 border-[#0F172A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="flex flex-col items-center gap-3 py-16 text-center px-4">
          <AlertCircle className="w-10 h-10 text-red-400" />
          <p className="text-slate-600 text-sm">{error}</p>
          <button
            onClick={() => setRefresh(r => r + 1)}
            className="text-sm text-[#2563EB] underline"
          >
            Riprova
          </button>
        </div>
      ) : !stats ? null : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 gap-3">
            <KpiCard
              label="Fatturato (mese)"
              value={`€${stats.kpi.month_revenue.toFixed(0)}`}
              accent="border-emerald-300"
            />
            <KpiCard
              label="Ordini (mese)"
              value={stats.kpi.month_orders}
              accent="border-violet-300"
            />
            <KpiCard
              label="Visite (mese)"
              value={stats.kpi.month_visits}
              accent="border-blue-300"
            />
            <KpiCard
              label="Conversione"
              value={`${stats.kpi.overall_conversion}%`}
              sub="nel periodo"
              accent="border-amber-300"
            />
          </div>

          {/* Tabs */}
          <div className="flex gap-0 border-b border-slate-200">
            {[
              { id: "summary", label: "Riepilogo",  Icon: BarChart2 },
              { id: "esiti",   label: "Esiti",      Icon: CheckCircle },
              { id: "clients", label: "Clienti",    Icon: Users },
            ].map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                data-testid={`stats-tab-${id}`}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold transition-colors border-b-2 -mb-px ${
                  tab === id ? "border-[#0F172A] text-[#0F172A]" : "border-transparent text-slate-400 hover:text-slate-600"
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>

          {/* ── TAB: Riepilogo ── */}
          {tab === "summary" && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart2 className="w-5 h-5 text-[#0F172A]" />
                  <h2 className="font-semibold text-[#0F172A] text-sm">Fatturato mensile</h2>
                </div>
                <MonthlyChart data={stats.monthly_data} />
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-violet-500" />
                  <h2 className="font-semibold text-[#0F172A] text-sm">Ordini giornalieri (ultimi 30gg)</h2>
                </div>
                <DailyChart data={stats.daily_orders} />
              </div>
            </div>
          )}

          {/* ── TAB: Esiti ── */}
          {tab === "esiti" && (
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-5">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                <h2 className="font-semibold text-[#0F172A] text-sm">Breakdown esiti visite</h2>
              </div>
              <OutcomesBreakdown data={stats.outcomes_breakdown} />
            </div>
          )}

          {/* ── TAB: Clienti ── */}
          {tab === "clients" && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              {/* Search + sort bar */}
              <div className="px-4 py-3 border-b border-slate-100 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      value={search}
                      onChange={e => { setSearch(e.target.value); setExpanded(null); }}
                      placeholder="Cerca cliente..."
                      data-testid="client-search-input"
                      className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-slate-400"
                    />
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <ArrowUpDown className="w-3.5 h-3.5 text-slate-400" />
                    <select
                      value={sortBy}
                      onChange={e => setSortBy(e.target.value)}
                      data-testid="client-sort-select"
                      className="border border-slate-200 rounded-xl px-2 py-2 text-xs bg-white cursor-pointer focus:outline-none"
                    >
                      <option value="orders">Ordini</option>
                      <option value="revenue">Fatturato</option>
                      <option value="conversion">Conversione</option>
                      <option value="visits">Visite</option>
                    </select>
                  </div>
                </div>
                <p className="text-xs text-slate-400">
                  {filteredClients.length} client{filteredClients.length !== 1 ? "i" : "e"} nel periodo
                  {search && ` · filtrati per "${search}"`}
                </p>
              </div>

              {filteredClients.length === 0 ? (
                <p className="text-slate-400 text-sm text-center py-10 px-4">
                  {search ? `Nessun cliente trovato per "${search}"` : "Nessuna visita registrata nel periodo"}
                </p>
              ) : (
                filteredClients.map(cs => (
                  <ClientStatRow
                    key={cs.client_id}
                    cs={cs}
                    expanded={expanded === cs.client_id}
                    onToggle={() => setExpanded(expanded === cs.client_id ? null : cs.client_id)}
                  />
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function StatisticsPage() {
  return (
    <PremiumGate>
      <StatsContent />
    </PremiumGate>
  );
}
